(() => {
  const PROP_GIFT = '_spectrum_mix_match_gift';

  const ALLOWED_LINE_PROPERTIES = new Set([
    '_pharmacy',
    '_administration_type',
    '_delivery_type',
    '_is_prescriptional',
    '_fulfillment_method'
  ]);

  const readLineProperties = () => {
    const form = document.querySelector('form[is="product-form"]');
    const properties = {};
    if (!form) return properties;

    for (const input of form.querySelectorAll('input[name^="properties["]')) {
      const match = input.name.match(/^properties\[(.+)\]$/);
      if (!match || input.disabled || input.value === '') continue;
      const key = match[1];
      // Only copy the same props normal ATC uses so identical vapes merge into one line.
      // Never invent Mix & Match props on vapes (that splits lines) and never touch fees.
      if (!ALLOWED_LINE_PROPERTIES.has(key) && !key.startsWith('_pharmacy')) continue;
      properties[key] = input.value;
    }

    return properties;
  };

  const getLineProperty = (item, key) => {
    if (!item || !item.properties) return null;
    if (Array.isArray(item.properties)) {
      const found = item.properties.find((entry) => entry && entry[0] === key);
      return found ? found[1] : null;
    }
    return item.properties[key] || null;
  };

  const isSpectrumVapeCartItem = (item) => {
    if (!item) return false;
    if (item.product_type === 'processing_fee' || item.product_type === 'consultation') return false;
    if (getLineProperty(item, PROP_GIFT) === '1') return false;
    const title = String(item.product_title || item.title || '');
    if (!title.includes('SpectrumBotanics')) return false;
    if (title.includes('4 x') || title.includes('Nachfüllpack') || title.includes('Nur Rezept')) return false;
    return true;
  };

  const linePropertyScore = (item) => {
    let score = 0;
    if (getLineProperty(item, '_pharmacy_pickup')) score += 4;
    if (getLineProperty(item, '_fulfillment_method')) score += 2;
    if (getLineProperty(item, '_pharmacy')) score += 2;
    if (getLineProperty(item, '_administration_type')) score += 1;
    if (getLineProperty(item, '_delivery_type')) score += 1;
    return score;
  };

  const pickPreferredLine = (lines) => {
    const ranked = [...lines].sort((a, b) => {
      const scoreDiff = linePropertyScore(b) - linePropertyScore(a);
      if (scoreDiff !== 0) return scoreDiff;
      return String(a.key).localeCompare(String(b.key));
    });
    return ranked[0];
  };

  const applyCartUpdates = async (updates) => {
    if (!updates || Object.keys(updates).length === 0) return null;

    const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ updates })
    });

    if (!response.ok) return null;
    return response.json();
  };

  const buildGiftCapUpdates = (cart) => {
    const updates = {};
    const giftItems = [];

    for (const item of cart.items) {
      if (item.product_type === 'processing_fee') continue;
      if (getLineProperty(item, PROP_GIFT) === '1') giftItems.push(item);
    }

    for (const item of giftItems) {
      if (item.quantity !== 1) updates[item.key] = 1;
    }

    return updates;
  };

  // Same Spectrum variant + different line properties => separate Shopify rows.
  // Collapse those rows onto the best existing line (pharmacy props preferred).
  const buildVapeMergeUpdates = (cart) => {
    const updates = {};
    const byVariant = new Map();

    for (const item of cart.items) {
      if (!isSpectrumVapeCartItem(item)) continue;
      const id = String(item.variant_id);
      if (!byVariant.has(id)) byVariant.set(id, []);
      byVariant.get(id).push(item);
    }

    for (const lines of byVariant.values()) {
      if (lines.length < 2) continue;
      const keeper = pickPreferredLine(lines);
      const total = lines.reduce((sum, line) => sum + Number(line.quantity || 0), 0);
      updates[keeper.key] = total;
      for (const line of lines) {
        if (line.key !== keeper.key) updates[line.key] = 0;
      }
    }

    return updates;
  };

  const syncMixMatchCart = async (cart) => {
    if (!cart || !Array.isArray(cart.items)) return cart;

    const updates = {
      ...buildGiftCapUpdates(cart),
      ...buildVapeMergeUpdates(cart)
    };

    if (Object.keys(updates).length === 0) return cart;

    const updated = await applyCartUpdates(updates);
    return updated || cart;
  };

  if (!window.__spectrumMixMatchCartGuard) {
    window.__spectrumMixMatchCartGuard = true;

    document.addEventListener('cart:change', async (event) => {
      if (event.detail?.baseEvent === 'spectrum-mix-match-sync') return;
      const cart = event.detail?.cart;
      if (!cart) return;

      const updated = await syncMixMatchCart(cart);
      if (updated && updated !== cart) {
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: {
            baseEvent: 'spectrum-mix-match-sync',
            cart: updated
          }
        }));
      }
    });
  }

  const fillTemplate = (template, values) => {
    let result = template || '';
    for (const [key, value] of Object.entries(values)) {
      result = result.replace(new RegExp(key, 'g'), String(value));
    }
    return result;
  };

  const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

  const formatMoneyCents = (cents) => {
    const amount = Number(cents) || 0;
    try {
      if (window.Shopify?.formatMoney) {
        return window.Shopify.formatMoney(amount, window.themeVariables?.settings?.moneyFormat);
      }
    } catch (error) {
      // fall through
    }
    return new Intl.NumberFormat(document.documentElement.lang || 'de-DE', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'EUR'
    }).format(amount / 100);
  };

  const findLookupItem = (lookupLists, variantId, preferGift) => {
    const id = String(variantId);
    let fallback = null;
    for (const list of lookupLists) {
      for (const item of list || []) {
        if (String(item.variant_id) !== id) continue;
        const isGift = getLineProperty(item, PROP_GIFT) === '1';
        if (preferGift && isGift) return item;
        if (!preferGift && !isGift) return item;
        if (!fallback) fallback = item;
      }
    }
    return fallback;
  };

  const buildPopupItemsFromRequest = (requestedItems, responseItems, cartItems) => {
    const lists = [responseItems || [], cartItems || []];

    return (requestedItems || []).map((req) => {
      const qty = Number(req.quantity) || 1;
      const isGift = req.properties?.[PROP_GIFT] === '1';
      const source = findLookupItem(lists, req.id, isGift) || {};
      const unitPrice = Number(source.price) || 0;
      let linePrice = unitPrice * qty;

      if (isGift) {
        if (Number(source.final_line_price) === 0) {
          linePrice = 0;
        } else if (source.final_line_price != null && Number(source.quantity) > 0) {
          linePrice = Math.round((Number(source.final_line_price) / Number(source.quantity)) * qty);
        }
      }

      return {
        product_title: source.product_title || source.title || '',
        variant_title: source.variant_title,
        title: source.title,
        image: source.image,
        url: source.url,
        quantity: qty,
        price: unitPrice,
        final_line_price: linePrice,
        properties: req.properties || source.properties || {},
        variant_id: req.id
      };
    });
  };

  const buildAddedSectionHtml = (items, strings) => {
    const lines = (items || []).map((item) => {
      const title = escapeHtml(item.product_title || item.title || '');
      const variantTitle = item.variant_title && item.variant_title !== 'Default Title'
        ? `<p class="text-sm text-subdued">${escapeHtml(item.variant_title)}</p>`
        : '';
      const qty = Number(item.quantity) || 1;
      const qtyLabel = qty > 1 ? `<p class="text-sm text-subdued">${qty}×</p>` : '';
      const isGift = getLineProperty(item, PROP_GIFT) === '1';
      const priceHtml = isGift && Number(item.final_line_price) === 0
        ? `<span class="text-sm bold" style="color: rgb(var(--success-text))">${escapeHtml(strings.free_badge || 'Gratis')}</span>`
        : `<span class="text-sm">${escapeHtml(formatMoneyCents(item.final_line_price ?? item.line_price ?? item.price))}</span>`;
      const image = item.image
        ? `<img src="${escapeHtml(item.image)}" alt="" width="80" height="80" loading="lazy" class="quick-buy-drawer__media rounded-xs">`
        : '';
      const href = escapeHtml(item.url || '#');

      return `
        <div class="quick-buy-drawer__variant text-start h-stack gap-6">
          ${image}
          <div class="v-stack gap-1">
            <div class="v-stack gap-0.5">
              <a href="${href}" class="bold justify-self-start">${title}</a>
              ${priceHtml}
            </div>
            ${qtyLabel}
            ${variantTitle}
          </div>
        </div>
      `;
    }).join('');

    const cartUrl = `${window.Shopify.routes.root}cart`;
    const addedLabel = escapeHtml(strings.added_to_cart || '');
    const viewCartLabel = escapeHtml(strings.view_cart || '');

    return `
      <div id="shopify-section-variant-added" class="shopify-section">
        <div class="quick-buy-drawer__info">
          <div class="banner banner--success">${addedLabel}</div>
          <div class="v-stack gap-4">
            ${lines}
          </div>
          <form action="${cartUrl}" method="post" class="buy-buttons buy-buttons--compact">
            <a href="${cartUrl}" class="button button--secondary">${viewCartLabel}</a>
          </form>
        </div>
      </div>
    `;
  };

  const NATIVE_ATC_HIDE_CLASS = 'smm-hide-native-atc';

  const setNativeAtcHidden = (hidden) => {
    const productInfo = document.querySelector('.product-info');
    if (!productInfo) return;
    productInfo.classList.toggle(NATIVE_ATC_HIDE_CLASS, Boolean(hidden));
  };

  class SpectrumMixMatch extends HTMLElement {
    #bound = false;
    #adding = false;
    #wasGiftStep = false;

    connectedCallback() {
      setNativeAtcHidden(true);

      if (this.#bound) return;
      this.#bound = true;

      this.bundleSize = Number(this.dataset.bundleSize || 3);
      this.maxSelection = Number(this.dataset.maxSelection || 6);
      if (this.maxSelection < this.bundleSize) this.maxSelection = this.bundleSize;
      this.giftsAvailable = this.dataset.giftsAvailable !== 'false';
      this.showPriceSum = this.dataset.showPriceSum === 'true';
      this.slots = Array.from({ length: this.bundleSize }, () => null);
      this.strings = this.#parseStrings(this.querySelector('[data-mix-match-strings]'));

      const current = this.#currentProductSlot();
      if (current) this.slots[0] = current;

      this.slotEls = Array.from(this.querySelectorAll('[data-slot]'));
      this.progressEl = this.querySelector('[data-progress]');
      this.progressFill = this.querySelector('[data-progress-fill]');
      this.progressBar = this.querySelector('[data-progress-bar]');
      this.priceSumEl = this.querySelector('[data-price-sum]');
      this.priceSumValueEl = this.querySelector('[data-price-sum-value]');
      this.vapeStep = this.querySelector('[data-vape-step]');
      this.batteryStep = this.querySelector('[data-battery-step]');
      this.errorEl = this.querySelector('[data-error]');
      this.addButton = this.querySelector('[data-add-bundle]');
      this.addButtonLabel = this.addButton?.querySelector('span') || this.addButton;

      this.addEventListener('click', (event) => {
        const pickButton = event.target.closest('[data-pick-vape]');
        if (pickButton && this.contains(pickButton)) {
          event.preventDefault();
          this.#pickVape(pickButton);
          return;
        }

        const slotEl = event.target.closest('[data-slot]');
        if (slotEl && this.contains(slotEl)) {
          event.preventDefault();
          const index = Number(slotEl.dataset.slot);
          if (this.slots[index]) this.#clearSlot(index);
          else if (this.vapeStep) this.vapeStep.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      });

      this.querySelectorAll('[data-battery-radio]').forEach((radio) => {
        radio.addEventListener('change', () => this.#onBatteryChange());
      });

      this.querySelectorAll('[data-battery-variant]').forEach((input) => {
        input.addEventListener('change', () => this.#onBatteryChange());
      });

      this.addButton?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.#addBundle();
      });
      this.#render();
    }

    disconnectedCallback() {
      this.#bound = false;
      if (!document.querySelector('spectrum-mix-match')) {
        setNativeAtcHidden(false);
      }
    }

    #parseStrings(node) {
      if (!node) return {};
      try {
        return JSON.parse(node.textContent || '{}');
      } catch (error) {
        return {};
      }
    }

    #filledCount() {
      return this.slots.filter(Boolean).length;
    }

    #pickVape(button) {
      if (this.#filledCount() >= this.maxSelection) return;

      let emptyIndex = this.slots.findIndex((slot) => !slot);
      if (emptyIndex === -1) {
        if (this.slots.length >= this.maxSelection) return;
        this.slots.push(null);
        emptyIndex = this.slots.length - 1;
      }

      this.slots[emptyIndex] = {
        productId: button.dataset.productId,
        variantId: button.dataset.variantId,
        title: button.dataset.title,
        image: button.dataset.image || '',
        price: Number(button.dataset.price) || 0,
        isCurrent: button.dataset.productId === this.dataset.currentProductId
      };

      this.#compactSlots();
      this.#render();
    }

    #clearSlot(index) {
      if (!this.slots[index]) return;
      this.slots[index] = null;
      this.#compactSlots();
      this.#render();
    }

    #compactSlots() {
      const filled = this.slots.filter(Boolean);
      const next = filled.slice();

      while (next.length < this.bundleSize) next.push(null);

      if (filled.length >= this.bundleSize && filled.length < this.maxSelection) {
        next.push(null);
      }

      this.slots = next;
    }

    #currentProductSlot() {
      if (this.dataset.currentAvailable !== 'true') return null;

      return {
        productId: this.dataset.currentProductId,
        variantId: this.dataset.currentVariantId,
        title: this.dataset.currentTitle,
        image: this.dataset.currentImage || '',
        price: Number(this.dataset.currentPrice) || 0,
        isCurrent: true
      };
    }

    #resetSlots() {
      this.slots = Array.from({ length: this.bundleSize }, () => null);
      const current = this.#currentProductSlot();
      if (current) this.slots[0] = current;
      this.#wasGiftStep = false;
    }

    #syncSlotButtons() {
      const wrap = this.querySelector('[data-slots]');
      if (!wrap) return;

      while (wrap.children.length < this.slots.length) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'spectrum-mix-match__slot';
        button.innerHTML = '<div class="spectrum-mix-match__slot-body"></div>';
        wrap.appendChild(button);
      }

      while (wrap.children.length > this.slots.length) {
        wrap.lastElementChild.remove();
      }

      this.slotEls = Array.from(wrap.children);
      this.slotEls.forEach((slotEl, index) => {
        slotEl.dataset.slot = String(index);
      });
      wrap.dataset.slotCount = String(this.slots.length);
    }

    #selectedBatteryVariantId() {
      const selectedRadio = this.querySelector('[data-battery-radio]:checked');
      if (!selectedRadio) return null;

      const productId = selectedRadio.value;
      const variantInput = this.querySelector(`[data-battery-variant][data-product-id="${productId}"]`);
      if (variantInput?.value) return variantInput.value;
      return selectedRadio.dataset.variantId || null;
    }

    #giftVariantIdSet() {
      const ids = new Set();
      this.querySelectorAll('[data-battery-variant]').forEach((el) => {
        if (el.tagName === 'SELECT') {
          for (const option of el.options) {
            if (option.value) ids.add(String(option.value));
          }
          return;
        }
        if (el.value) ids.add(String(el.value));
      });
      this.querySelectorAll('[data-battery-radio][data-variant-id]').forEach((radio) => {
        if (radio.dataset.variantId) ids.add(String(radio.dataset.variantId));
      });
      return ids;
    }

    #onBatteryChange() {
      this.querySelectorAll('[data-battery-product]').forEach((card) => {
        const radio = card.querySelector('[data-battery-radio]');
        const variantWrap = card.querySelector('[data-variant-wrap]');
        const variantInput = card.querySelector('[data-battery-variant]');
        const selected = Boolean(radio?.checked);
        card.classList.toggle('spectrum-mix-match__gift-card--selected', selected);
        if (variantWrap) variantWrap.hidden = !selected;
        if (selected && radio && variantInput) {
          radio.dataset.variantId = variantInput.value;
        }
      });
      this.#updateCta();
    }

    #ensureGiftSelected() {
      const radios = this.querySelectorAll('[data-battery-radio]');
      if (radios.length === 0) return;
      const anyChecked = Array.from(radios).some((radio) => radio.checked);
      if (!anyChecked) radios[0].checked = true;
      this.#onBatteryChange();
    }

    #updateCta() {
      if (!this.addButton) return;

      const filled = this.#filledCount();
      const remaining = this.bundleSize - filled;
      const giftStep = filled >= this.bundleSize && this.giftsAvailable;
      const giftSelected = Boolean(this.#selectedBatteryVariantId());
      const labelNode = this.addButtonLabel || this.addButton;

      if (filled === 0) {
        this.addButton.disabled = true;
        labelNode.textContent = fillTemplate(this.strings.cta_need_vapes, {
          __COUNT__: this.bundleSize
        });
        return;
      }

      if (giftStep && !giftSelected) {
        this.addButton.disabled = true;
        labelNode.textContent = this.strings.cta_need_gift || '';
        return;
      }

      this.addButton.disabled = false;

      if (giftStep) {
        labelNode.textContent = fillTemplate(this.strings.cta_add_with_gift || this.strings.cta_add || '', {
          __COUNT__: filled
        });
        return;
      }

      if (filled === 1) {
        labelNode.textContent = this.strings.cta_add_one || fillTemplate(this.strings.cta_add_partial || '', {
          __COUNT__: filled
        });
        return;
      }

      labelNode.textContent = fillTemplate(this.strings.cta_add_partial || this.strings.cta_add, {
        __COUNT__: filled
      });
    }

    #setError(message) {
      if (!this.errorEl) return;
      if (!message) {
        this.errorEl.hidden = true;
        this.errorEl.textContent = '';
        return;
      }
      this.errorEl.hidden = false;
      this.errorEl.textContent = message;
    }

    #render() {
      const filled = this.#filledCount();
      const remaining = Math.max(this.bundleSize - filled, 0);
      const giftStep = filled >= this.bundleSize && this.giftsAvailable;
      const bundleFull = filled >= this.bundleSize;
      const atMax = filled >= this.maxSelection;
      const counts = new Map();

      this.#syncSlotButtons();

      for (const slot of this.slots) {
        if (!slot) continue;
        counts.set(slot.productId, (counts.get(slot.productId) || 0) + 1);
      }

      this.slotEls.forEach((slotEl, index) => {
        const slot = this.slots[index];
        const body = slotEl.querySelector('.spectrum-mix-match__slot-body');
        slotEl.classList.toggle('spectrum-mix-match__slot--filled', Boolean(slot));
        slotEl.classList.toggle('spectrum-mix-match__slot--active', !slot && !atMax);
        if (!body) return;

        if (!slot) {
          const emptyLabel = this.strings.empty_slot || this.strings.extra_slot || '';
          body.innerHTML = `
            <span class="spectrum-mix-match__slot-plus" aria-hidden="true">+</span>
            <span class="spectrum-mix-match__slot-title text-xs text-subdued">${emptyLabel}</span>
          `;
          slotEl.setAttribute('aria-label', emptyLabel);
          return;
        }

        const imageHtml = slot.image
          ? `<img class="spectrum-mix-match__slot-image" src="${slot.image}" alt="" width="60" height="60" loading="lazy">`
          : '';

        body.innerHTML = `
          ${imageHtml}
          <span class="spectrum-mix-match__slot-title text-xs bold">${slot.title}</span>
        `;
        slotEl.setAttribute('aria-label', `${slot.title}. ${this.strings.remove || ''}`);
      });

      this.querySelectorAll('[data-pick-vape]').forEach((button) => {
        const count = counts.get(button.dataset.productId) || 0;
        const countEl = button.querySelector('[data-pick-count]');
        button.classList.toggle('spectrum-mix-match__card--selected', count > 0);
        button.setAttribute('aria-pressed', count > 0 ? 'true' : 'false');
        button.disabled = atMax;

        if (countEl) {
          countEl.hidden = count === 0;
          countEl.textContent = count > 0 ? String(count) : '';
        }
      });

      if (this.progressFill) {
        this.progressFill.style.width = `${Math.min(100, Math.round((filled / this.bundleSize) * 100))}%`;
        this.progressFill.classList.toggle(
          'spectrum-mix-match__progress-fill--complete',
          giftStep || (bundleFull && !this.giftsAvailable)
        );
      }

      if (this.progressBar) {
        this.progressBar.classList.toggle(
          'spectrum-mix-match__progress-bar--complete',
          giftStep || (bundleFull && !this.giftsAvailable)
        );
      }

      if (this.progressEl) {
        if (giftStep || (bundleFull && !this.giftsAvailable)) {
          this.progressEl.textContent = this.strings.progress_complete || '';
        } else if (remaining === 1) {
          this.progressEl.textContent = this.strings.progress_remaining_one || fillTemplate(this.strings.progress_remaining, {
            __COUNT__: remaining
          });
        } else {
          this.progressEl.textContent = fillTemplate(this.strings.progress_remaining, {
            __COUNT__: remaining
          });
        }
      }

      if (this.vapeStep) this.vapeStep.hidden = false;
      if (this.batteryStep) {
        this.batteryStep.hidden = !giftStep;
        if (giftStep && !this.#wasGiftStep) {
          this.#ensureGiftSelected();
          this.batteryStep.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }
      this.#wasGiftStep = giftStep;

      this.#updatePriceSum();
      this.#updateCta();
      this.#setError('');
    }

    #bundlePriceCents() {
      return this.slots.reduce((sum, slot) => sum + (Number(slot?.price) || 0), 0);
    }

    #updatePriceSum() {
      if (!this.showPriceSum || !this.priceSumEl || !this.priceSumValueEl) return;

      const filled = this.#filledCount();
      if (filled === 0) {
        this.priceSumEl.hidden = true;
        this.priceSumValueEl.textContent = '';
        return;
      }

      this.priceSumEl.hidden = false;
      this.priceSumValueEl.textContent = formatMoneyCents(this.#bundlePriceCents());
    }

    async #addBundle() {
      if (this.#adding) return;
      this.#setError('');

      const filled = this.#filledCount();
      if (filled === 0) {
        this.#setError(this.strings.error_incomplete || '');
        return;
      }

      const giftStep = filled >= this.bundleSize && this.giftsAvailable;
      const batteryVariantId = giftStep ? this.#selectedBatteryVariantId() : null;
      if (giftStep && !batteryVariantId) {
        this.#setError(this.strings.error_battery || '');
        return;
      }

      const pharmacyProperties = readLineProperties();
      const vapeProperties = { ...pharmacyProperties };

      if (this.dataset.administrationType && !vapeProperties._administration_type) {
        vapeProperties._administration_type = this.dataset.administrationType;
      }

      if (this.dataset.deliveryType && !vapeProperties._delivery_type) {
        vapeProperties._delivery_type = this.dataset.deliveryType;
        vapeProperties._is_prescriptional = '1';
      }

      const giftVariantIds = this.#giftVariantIdSet();
      const quantityByVariant = new Map();
      for (const slot of this.slots) {
        if (!slot) continue;
        const variantId = String(slot.variantId);
        if (giftVariantIds.has(variantId)) continue;

        const existing = quantityByVariant.get(variantId) || {
          id: Number(variantId),
          quantity: 0,
          properties: vapeProperties
        };
        existing.quantity += 1;
        quantityByVariant.set(variantId, existing);
      }

      const requestedItems = Array.from(quantityByVariant.values());
      if (requestedItems.length === 0) {
        this.#setError(this.strings.error_incomplete || '');
        return;
      }

      const giftVariantId = giftStep ? String(batteryVariantId) : null;
      if (giftVariantId) {
        requestedItems.push({
          id: Number(giftVariantId),
          quantity: 1,
          properties: {
            [PROP_GIFT]: '1'
          }
        });
      }

      const firstVariantId = requestedItems[0]?.id;
      this.#adding = true;
      this.addButton.disabled = true;
      this.addButton.setAttribute('aria-busy', 'true');

      try {
        let cartContent = await (await fetch(`${window.Shopify.routes.root}cart.js`)).json();
        cartContent = await syncMixMatchCart(cartContent);

        const quantityUpdates = {};
        const itemsToAdd = [];

        for (const item of requestedItems) {
          const isGift = item.properties?.[PROP_GIFT] === '1';
          const variantId = String(item.id);

          if (isGift) {
            const existingGift = (cartContent.items || []).find((line) => (
              String(line.variant_id) === variantId && getLineProperty(line, PROP_GIFT) === '1'
            ));
            if (existingGift) {
              if (existingGift.quantity !== 1) quantityUpdates[existingGift.key] = 1;
            } else {
              itemsToAdd.push(item);
            }
            continue;
          }

          const existingLines = (cartContent.items || []).filter((line) => (
            String(line.variant_id) === variantId && isSpectrumVapeCartItem(line)
          ));

          if (existingLines.length > 0) {
            const keeper = pickPreferredLine(existingLines);
            const currentTotal = existingLines.reduce((sum, line) => sum + Number(line.quantity || 0), 0);
            quantityUpdates[keeper.key] = currentTotal + Number(item.quantity || 0);
            for (const line of existingLines) {
              if (line.key !== keeper.key) quantityUpdates[line.key] = 0;
            }
          } else {
            itemsToAdd.push(item);
          }
        }

        let responseJson = { items: [], sections: {} };

        if (Object.keys(quantityUpdates).length > 0) {
          const updated = await applyCartUpdates(quantityUpdates);
          if (updated) cartContent = updated;
        }

        if (itemsToAdd.length > 0) {
          let sectionsToBundle = ['variant-added'];
          document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
            bubbles: true,
            detail: { sections: sectionsToBundle }
          }));

          const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({
              items: itemsToAdd,
              sections: sectionsToBundle.join(','),
              sections_url: firstVariantId
                ? `${window.Shopify.routes.root}variants/${firstVariantId}`
                : window.location.pathname
            })
          });

          responseJson = await response.json();
          if (!response.ok) {
            throw new Error(responseJson.description || responseJson.message || this.strings.error_generic || '');
          }

          cartContent = await (await fetch(`${window.Shopify.routes.root}cart.js`)).json();
        }

        cartContent = await syncMixMatchCart(cartContent);

        if (window.themeVariables?.settings?.cartType === 'page' || window.themeVariables?.settings?.pageType === 'cart') {
          window.location.href = `${window.Shopify.routes.root}cart`;
          return;
        }

        // Popup must show quantities just added (request payload), not cart totals.
        const popupItems = buildPopupItemsFromRequest(
          requestedItems,
          responseJson.items || [],
          cartContent.items || []
        );

        const addedSectionHtml = buildAddedSectionHtml(popupItems, this.strings);

        cartContent.sections = {
          ...(responseJson.sections || {}),
          'variant-added': addedSectionHtml
        };

        document.documentElement.dispatchEvent(new CustomEvent('variant:add', {
          bubbles: true,
          detail: {
            items: popupItems,
            cart: cartContent
          }
        }));

        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: {
            baseEvent: 'variant:add',
            cart: cartContent
          }
        }));

        this.#resetSlots();
        this.#render();
      } catch (error) {
        this.#setError(error.message || this.strings.error_generic || '');
      } finally {
        this.#adding = false;
        this.addButton?.removeAttribute('aria-busy');
        this.#updateCta();
      }
    }
  }

  if (!window.customElements.get('spectrum-mix-match')) {
    window.customElements.define('spectrum-mix-match', SpectrumMixMatch);
  }
})();
